import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DynamicGiftDto, MarketType } from '@nova/types';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { APP_ROUTE } from 'app/app-routing.constant';
import { UserRole } from 'app/core/authentication';
import { removeTrailingSlash } from 'app/core/kh-utils/kh-utils';
import { ModalService } from 'app/core/modals/services/modal.service';
import { STUDENT_ROUTE } from 'app/routing-constants/student-routing.constant';
import { DataTrackLabelEnum } from 'app/shared/enums/data-track-label.enum';
import { NewsArticleModalComponent } from 'app/shared/modals/news-article-modal/news-article-modal.component';
import { NewsItem } from 'app/shared/models/news-widget/news-item-model';
import { DynamicGiftsService } from 'app/shared/services/dynamic-gifts.service';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';
import { NewsWidgetService } from 'app/shared/services/news-widget-service';
import { GAME_SECTION_ROUTE } from 'app/student/game-section/game-section-routing.constants';
import { CarouselOptions, CarouselResponsiveOptions, CarouselSlidesOutputData } from '../carousel/carousel.models';

import { environment } from 'env/environment';

export enum NewsWidgetDisplayType {
  STUDENT = 'student',
  TEACHER = 'teacher'
}

@UntilDestroy()
@Component({
  selector: 'kh-news-widget',
  templateUrl: './news-widget.component.html',
  styleUrl: './news-widget.component.less',
  standalone: false
})
export class NewsWidgetComponent implements OnInit, OnDestroy {
  protected readonly newsWidgetService = inject(NewsWidgetService);
  protected readonly modalService = inject(ModalService);
  protected readonly analyticsService = inject(KhAnalyticsService);

  @Input() public role: UserRole = UserRole.student;
  @Input() public classId!: string;
  @Input() public isCollapsed = false;
  @Input() public displayType: NewsWidgetDisplayType = NewsWidgetDisplayType.STUDENT;
  @Output() public articleSelected = new EventEmitter<NewsItem>();
  @Output() public articleSeen = new EventEmitter<NewsItem>();
  @Output() public dotsClicked = new EventEmitter<void>();
  public NewsWidgetDisplayType = NewsWidgetDisplayType;
  public isLoaded = false;
  public internalLinks: Record<string, string> = {};
  public giftClaimMap = new Map<string, { exists: boolean; claimed: boolean }>();
  public giftInfoMap = new Map<string, DynamicGiftDto>();

  protected readonly dynamicGiftsService = inject(DynamicGiftsService);

  protected carouselResponsive: Record<number, CarouselResponsiveOptions> = {
    0: {
      items: 1
    },
    600: {
      items: 2.7
    }
  };

  protected carouselOptions: CarouselOptions = {
    items: 2.7,
    dotsEach: false,
    responsive: this.carouselResponsive
  };

  public items: NewsItem[] = [];

  public ngOnDestroy(): void {
    this.analyticsService.flushEvents();
  }

  public ngOnInit(): void {
    const baseUrl = removeTrailingSlash(environment.AppBaseHref);

    this.internalLinks.launchbay = (baseUrl + GAME_SECTION_ROUTE.MAIN.route(this.classId).join('/')).replace(/([^:]\/)\/+/g, '$1');
    this.internalLinks.editbots = (baseUrl + GAME_SECTION_ROUTE.EDIT_BOTS.route(this.classId).join('/')).replace(/([^:]\/)\/+/g, '$1');
    const marketRoute = (baseUrl + GAME_SECTION_ROUTE.MARKET.route(this.classId).join('/')).replace(/([^:]\/)\/+/g, '$1');
    this.internalLinks.alldaymarket = marketRoute + '?marketType=' + MarketType.AllDayMarket;
    this.internalLinks.afterschoolmarket = marketRoute + '?marketType=' + MarketType.AfterSchoolMarket;
    this.internalLinks.seasonalmarket = marketRoute + '?marketType=' + MarketType.SeasonalMarket;

    const classroomGardenRoute = (baseUrl + GAME_SECTION_ROUTE.CLASSROOM_GARDEN.route(this.classId).join('/'))
      .replace(/([^:]\/)\/+/g, '$1');
    this.internalLinks.classroomgarden = classroomGardenRoute;

    const gameSelectRoute = (baseUrl + GAME_SECTION_ROUTE.GAME_SELECT.route(this.classId).join('/'))
      .replace(/([^:]\/)\/+/g, '$1');
    this.internalLinks.gameselectionscreen = gameSelectRoute;
    // Terraforming is multiplayer-only and starts from the game selection screen.
    this.internalLinks.gameterraforming = gameSelectRoute;

    this.internalLinks.arcade = (baseUrl + GAME_SECTION_ROUTE.ARCADE.route(this.classId).join('/'))
      .replace(/([^:]\/)\/+/g, '$1');

    const studentBaseRoute = (baseUrl + STUDENT_ROUTE.LOGGED.route(this.classId).join('/'))
      .replace(/([^:]\/)\/+/g, '$1');

    this.internalLinks.solvetab = `${studentBaseRoute}/solve`;

    this.internalLinks.mathlon = `${studentBaseRoute}/mathalon`;

    const novaOrBase = (baseUrl + APP_ROUTE.NOVA_GAME_OR.route().join('/'))
      .replace(/([^:]\/)\/+/g, '$1');
    this.internalLinks.gameoutpostrun = `${novaOrBase}?classId=${this.classId}&game=OR&ct=SP`;

    const novaZrBase = (baseUrl + APP_ROUTE.NOVA_GAME_ZR.route().join('/'))
      .replace(/([^:]\/)\/+/g, '$1');
    this.internalLinks.gamezonerush = `${novaZrBase}?classId=${this.classId}&game=ZR&ct=SP`;

    this.loadNews();
  }

  public loadNews(): void {
    this.newsWidgetService.getStudentNews(this.classId)
      .pipe(
        untilDestroyed(this))
      .subscribe((items: NewsItem[]) => {
        this.items = items;
        this.isLoaded = true;
        this.checkGiftClaims();
      });
  }

  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public expandNewsArticle(item: NewsItem): void {
    const key = item.id.toLowerCase();
    const cachedClaim = this.giftClaimMap.get(key);
    const cachedInfo = this.giftInfoMap.get(key) ?? null;

    // Open the modal immediately so the article content is visible regardless
    // of Nova response times or outages.
    const modalRef = this.openArticleModal(item);

    // Resolve claim status: reuse the cached value when available, otherwise
    // fetch. The second fetch only happens when the click landed before the
    // initial checkGiftClaims() call resolved.
    const claim$ = cachedClaim
      ? of(cachedClaim)
      : this.dynamicGiftsService.checkClaim([item.id]).pipe(
        map((res) => {
          const hit = res.gifts.find((g) => g.giftId.toLowerCase() === key);
          return { exists: hit?.exists ?? false, claimed: hit?.claimed ?? false };
        })
      );

    // Resolve deep gift details: skip the fetch when we already know the gift
    // doesn't exist. When claim status is unknown we optimistically fetch so
    // the panel can reveal the moment both observables settle.
    const info$ = (cachedClaim && !cachedClaim.exists)
      ? of<DynamicGiftDto | null>(null)
      : cachedInfo
        ? of(cachedInfo)
        : this.dynamicGiftsService.getGiftInfo(item.id);

    forkJoin({ claim: claim$, info: info$ })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ({ claim, info }) => {
          const { exists, claimed } = claim;
          this.giftClaimMap.set(key, { exists, claimed });

          if (exists && info) {
            this.giftInfoMap.set(key, info);
            if (modalRef.content) {
              modalRef.content.giftClaimed = claimed;
              modalRef.content.giftInfo = info;
            }
          }

          // Mark as seen unless there's an unclaimed gift waiting (in which
          // case markAsSeen will fire from onGiftClaimed after the student
          // clicks Claim).
          const hasUnclaimedGift = exists && !claimed;
          if (!hasUnclaimedGift) {
            this.markAsSeen(item);
          }
        },
        // If Nova is unreachable we can't confirm gift state, so degrade to
        // "no gift" behavior: keep the panel hidden and mark the article as
        // seen so the student isn't stuck with a lingering unread indicator.
        error: () => {
          this.markAsSeen(item);
        }
      });
  }

  private openArticleModal(item: NewsItem): BsModalRef<NewsArticleModalComponent> {
    const key = item.id.toLowerCase();
    const giftStatus = this.giftClaimMap.get(key);
    const giftInfo = this.giftInfoMap.get(key) ?? null;
    return this.modalService.displayCustomModal<NewsArticleModalComponent>(NewsArticleModalComponent, {
      size: 'lg',
      className: 'news-article-modal',
      data: {
        data: item,
        classId: this.classId,
        callBack: () => { this.articleSeen.emit(item); },
        giftClaimed: giftStatus?.claimed ?? false,
        giftInfo,
        onGiftClaimed: () => {
          this.giftClaimMap.set(key, { exists: true, claimed: true });
          this.markAsSeen(item);
        }
      }
    });
  }

  public markAsSeen(item: NewsItem): void {
    if (item.seen) {
      return;
    }

    this.newsWidgetService.markAsSeen(item.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.items.forEach((newsItem: NewsItem) => {
          if (newsItem.id === item.id) {
            newsItem.seen = true;
          }
        });
      });
  }

  public handleSlideChange(event: CarouselSlidesOutputData) {
    // Calculate which slides are currently visible based on carousel options and startPosition
    if (!event || !event.slides) { return; }

    const startIndex = event.startPosition ?? 0;
    const visibleSlides = this.items.slice(startIndex, startIndex + this.getVisibleItemCount());

    visibleSlides.forEach((newsItem) => {
      // Only add analytics event if not already present for this newsItem
      if (newsItem && !this.analyticsService.events.some((e) => e.jsonData && (e.jsonData as NewsItem).id === newsItem.id)) {
        this.analyticsService.storeEvent({
          eventType: DataTrackLabelEnum.StudentArticleViewedInTheWidget,
          jsonData: {
            classId: this.classId,
            id: newsItem.id,
            subject: newsItem.subject
          }
        });
      }
    });
  }

  public handleInitialized(event: CarouselSlidesOutputData) {
    this.handleSlideChange(event);
  }

  private getVisibleItemCount(): number {
    const viewportWidth = typeof window === 'undefined' ? 1920 : window.innerWidth;
    const responsive = this.carouselOptions.responsive ?? {};
    const breakpoints = Object.keys(responsive)
      .map((key) => Number(key))
      .filter((breakpoint) => !Number.isNaN(breakpoint))
      .sort((a, b) => a - b);

    let items = this.carouselOptions.items ?? 1;
    breakpoints.forEach((breakpoint) => {
      const breakpointItems = responsive[breakpoint]?.items;
      if (viewportWidth >= breakpoint && typeof breakpointItems === 'number') {
        items = breakpointItems;
      }
    });

    return Math.max(1, Math.ceil(items));
  }

  public isGiftAvailable(item: NewsItem): boolean {
    const status = this.giftClaimMap.get(item.id.toLowerCase());
    return status ? status.exists && !status.claimed : false;
  }

  protected checkGiftClaims(): void {
    const giftIds = this.items.map((item) => item.id);

    if (giftIds.length === 0) { return; }

    this.dynamicGiftsService.checkClaim(giftIds)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        response.gifts.forEach((gift) => {
          this.giftClaimMap.set(gift.giftId.toLowerCase(), {
            exists: gift.exists,
            claimed: gift.claimed
          });
        });
      });
  }
}
